import { MedplumClient } from "@medplum/core";
import { EpisodeOfCare } from "@medplum/fhirtypes";

import {
  fetchPregnancyRecordList,
  fetchPregnancyRecordListsForEpisodeOfCare,
  isPregnancyEpisodeOfCare,
  removeEpisodeOfCareFromPregnancyRecord,
  upsertEpisodeOfCareToPregnancyRecord,
} from "@/pregnancy";
import { getEpisodeOfCare } from "@/utils/episode-of-care";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { EpisodeOfCareZodFormType } from "@/zod-schemas/episode-of-care-creation";

export interface CreateOrUpdateEpisodeOfCareResult {
  episodeOfCare: EpisodeOfCare;
  /** True when pregnancy List attach/unlink threw after the episode was already saved. */
  pregnancyRecordLinkFailed: boolean;
  pregnancyRecordLinkError?: unknown;
}

export const createOrUpdateEpisodeOfCare = async (
  medplum: MedplumClient,
  values: EpisodeOfCareZodFormType,
  subjectId?: string,
  practitionerId?: string,
  episodeOfCareId?: string,
): Promise<CreateOrUpdateEpisodeOfCareResult> => {
  if (!subjectId || !practitionerId) {
    throw new Error("Subject ID and practitioner ID are required");
  }

  const existingEpisodeOfCare = episodeOfCareId
    ? await medplum.readResource("EpisodeOfCare", episodeOfCareId)
    : undefined;

  const episodeOfCare = getEpisodeOfCare({
    values,
    subjectId,
    practitionerId,
    episodeOfCareId,
    existingEpisodeOfCare,
  });

  const savedEpisodeOfCare = existingEpisodeOfCare
    ? await medplum.updateResource(episodeOfCare)
    : await medplum.createResource(episodeOfCare);

  try {
    await syncPregnancyEpisodeOfCareOnPregnancyRecord(medplum, savedEpisodeOfCare, subjectId);
    return {
      episodeOfCare: savedEpisodeOfCare,
      pregnancyRecordLinkFailed: false,
    };
  } catch (error) {
    return {
      episodeOfCare: savedEpisodeOfCare,
      pregnancyRecordLinkFailed: true,
      pregnancyRecordLinkError: error,
    };
  }
};

/**
 * Keeps pregnancy Episode of Care membership on the PregnancyRecord List in sync.
 *
 * Pregnancy-typed episodes are appended to the active pregnancy List when one exists
 * (matching encounter/lab silent skip when none exists). Attach targets only the first
 * current List because the product invariant is one active pregnancy record per patient.
 * Non-pregnancy episodes are removed from any pregnancy Lists that still reference them,
 * since unlink is cleanup and should be thorough.
 *
 * @param medplum - Medplum client
 * @param episodeOfCare - Saved Episode of Care resource
 * @param subjectId - Patient resource ID
 */
async function syncPregnancyEpisodeOfCareOnPregnancyRecord(
  medplum: MedplumClient,
  episodeOfCare: EpisodeOfCare,
  subjectId: string,
): Promise<void> {
  if (!episodeOfCare.id) return;

  if (isPregnancyEpisodeOfCare(episodeOfCare)) {
    const pregnancyLists = await fetchPregnancyRecordList(
      medplum,
      joinReferenceString("Patient", subjectId),
      { status: "current" },
    );

    // No active pregnancy List: intentional no-op (same as encounter/lab attach paths).
    if (!pregnancyLists?.[0]) return;

    await upsertEpisodeOfCareToPregnancyRecord(medplum, pregnancyLists[0], episodeOfCare);
    return;
  }

  const linkedPregnancyLists = await fetchPregnancyRecordListsForEpisodeOfCare(
    medplum,
    subjectId,
    episodeOfCare.id,
  );

  await Promise.all(
    linkedPregnancyLists.map((pregnancyList) =>
      removeEpisodeOfCareFromPregnancyRecord(medplum, pregnancyList, episodeOfCare),
    ),
  );
}

export const fetchAllEpisodeOfCareByClientId = async (
  medplum: MedplumClient,
  clientId?: string,
  status?: EpisodeOfCare["status"],
) => {
  if (!clientId) return [];

  const episodeOfCare = await medplum.searchResources("EpisodeOfCare", {
    patient: joinReferenceString("Patient", clientId),
    status,
  });

  const episodeOfCareList = getResourcesFromBundle("EpisodeOfCare", episodeOfCare.bundle);

  return episodeOfCareList;
};
